import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryIcon from "@mui/icons-material/History";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo } from "react";

import { colors } from "@components/ui/colors";
import { INote, INoteHistory } from "./interfaces/notes.interface";
import { formatRelativeDate, NOTE_TYPES } from "./notesUtils";

interface ClientNoteHistoryContentProps {
  note: INote | null;
}

const formatAbsoluteDate = (dateStr?: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getActionInfo = (item: INoteHistory) => {
  switch (item.action) {
    case "created":
      return {
        label: "Created Note",
        icon: <NoteAddOutlinedIcon sx={{ fontSize: 14 }} />,
        color: colors.gray[700],
        bg: colors.gray[100],
        borderColor: colors.gray[200],
      };
    case "content_updated":
      return {
        label: "Edited Note",
        icon: <EditOutlinedIcon sx={{ fontSize: 14 }} />,
        color: colors.orange[600],
        bg: colors.orange[50],
        borderColor: colors.orange[200],
      };
    case "pinned_toggle": {
      const isPinned = item.newContent === "true";
      return {
        label: isPinned ? "Pinned Note" : "Unpinned Note",
        icon: isPinned ? (
          <PushPinIcon sx={{ fontSize: 14, color: colors.orange[500] }} />
        ) : (
          <PushPinOutlinedIcon sx={{ fontSize: 14 }} />
        ),
        color: isPinned ? colors.orange[700] : colors.gray[700],
        bg: isPinned ? colors.orange[50] : colors.gray[100],
        borderColor: isPinned ? colors.orange[200] : colors.gray[200],
      };
    }
    case "type_updated":
      return {
        label: "Changed Tag",
        icon: <LabelOutlinedIcon sx={{ fontSize: 14 }} />,
        color: colors.info?.base || "#2563EB",
        bg: "#EFF6FF",
        borderColor: "#BFDBFE",
      };
    default:
      return {
        label: item.action?.replace(/_/g, " ") || "Updated",
        icon: <HistoryIcon sx={{ fontSize: 14 }} />,
        color: colors.gray[600],
        bg: colors.gray[100],
        borderColor: colors.gray[200],
      };
  }
};

export const ClientNoteHistoryContent = ({ note }: ClientNoteHistoryContentProps) => {
  const historyList = useMemo(() => {
    if (!note?.history || !Array.isArray(note.history)) return [];
    return [...note.history].sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [note?.history]);

  if (!note) return null;

  const currentTypeConfig = NOTE_TYPES[note.type] || NOTE_TYPES.general;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        p: 2.5,
        overflowY: "auto",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      {/* Current note snapshot header */}
      <Box
        sx={{
          mb: 2,
          p: 1.75,
          borderRadius: 2,
          backgroundColor: colors.gray[50],
          border: "1px solid",
          borderColor: colors.gray[200],
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.75,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: colors.gray[500],
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Current Content
          </Typography>
          <Chip
            label={currentTypeConfig.label}
            size="small"
            sx={{
              height: 20,
              borderRadius: 1,
              fontSize: 10,
              fontWeight: 600,
              backgroundColor: currentTypeConfig.bg,
              color: currentTypeConfig.color,
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: 13,
            lineHeight: 1.5,
            color: colors.gray[800],
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {note.content}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.gray[400], px: 1 }}>
          {historyList.length > 0
            ? `${historyList.length} History Event${historyList.length > 1 ? "s" : ""}`
            : "No History Recorded"}
        </Typography>
      </Divider>

      {historyList.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", color: colors.gray[400] }}>
          <HistoryIcon sx={{ fontSize: 32, opacity: 0.5, mb: 1 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>No update history available</Typography>
          <Typography sx={{ fontSize: 11 }}>This note has not been modified since its creation.</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {historyList.map((item, index) => {
            const actionInfo = getActionInfo(item);
            const authorName = item.user?.name || "Team Member";
            const authorInitials = authorName
              .split(" ")
              .filter(Boolean)
              .map((w) => w[0])
              .slice(0, 2)
              .join("")
              .toUpperCase();

            return (
              <Box
                key={item.uid || `${item.updatedAt}-${index}`}
                sx={{
                  position: "relative",
                  pl: 2.25,
                  borderLeft: "2px solid",
                  borderColor: index === 0 ? colors.orange[400] : colors.gray[200],
                }}
              >
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: "absolute",
                    left: -5,
                    top: 6,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: index === 0 ? colors.orange[500] : colors.gray[300],
                    border: "2px solid var(--fx-surface-base, #FFFFFF)",
                  }}
                />

                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: colors.gray[200],
                    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                    transition: "box-shadow 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  {/* Header: User + Action Chip + Timestamp */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={item.user?.avatar || undefined}
                        sx={{
                          width: 22,
                          height: 22,
                          fontSize: 9.5,
                          fontWeight: 700,
                          backgroundColor: colors.orange[500],
                          color: "#fff",
                        }}
                      >
                        {authorInitials || "U"}
                      </Avatar>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: colors.gray[900] }}>
                        {authorName}
                      </Typography>
                      <Chip
                        icon={actionInfo.icon}
                        label={actionInfo.label}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 10,
                          fontWeight: 600,
                          color: actionInfo.color,
                          backgroundColor: actionInfo.bg,
                          border: `1px solid ${actionInfo.borderColor}`,
                          "& .MuiChip-icon": {
                            color: "inherit",
                            ml: 0.75,
                          },
                        }}
                      />
                    </Box>

                    <Tooltip title={formatAbsoluteDate(item.updatedAt)} placement="top">
                      <Typography sx={{ fontSize: 11, color: colors.gray[400], cursor: "default" }}>
                        {formatRelativeDate(item.updatedAt)}
                      </Typography>
                    </Tooltip>
                  </Box>

                  {/* Clean text body */}
                  {item.action === "content_updated" && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          lineHeight: 1.5,
                          color: colors.gray[800],
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.newContent || item.previousContent}
                      </Typography>
                    </Box>
                  )}

                  {item.action === "created" && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          lineHeight: 1.5,
                          color: colors.gray[800],
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.newContent || item.previousContent || "Initial note created"}
                      </Typography>
                    </Box>
                  )}

                  {item.action === "pinned_toggle" && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: 13, color: colors.gray[800] }}>
                        Note status changed to{" "}
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: item.newContent === "true" ? colors.orange[600] : colors.gray[900],
                            fontSize: "inherit",
                          }}
                        >
                          {item.newContent === "true" ? "Pinned" : "Unpinned"}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  {item.action === "type_updated" && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: 13, color: colors.gray[800] }}>
                        Note tag changed to{" "}
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: colors.orange[600],
                            fontSize: "inherit",
                          }}
                        >
                          {item.newContent}
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
